import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
  PERSON_TASTE_STORAGE_KEY,
  emptyPersonTaste,
  isPersonTuned,
  normalizePersonTaste,
  patchPersonTaste,
  readAllPersonTaste,
  writeAllPersonTaste,
  type PersonTaste,
  type PersonTasteMap,
} from '../person-taste.data'

/**
 * Person taste — one reactive source for People, the Gift quiz and the search's
 * "Personalized for" selector. BE-202: reads / writes the browser now; with the API it
 * keeps the same methods and loads from People instead.
 */
export const usePersonTasteStore = defineStore('personTaste', () => {
  const byId = ref<PersonTasteMap>(readAllPersonTaste())

  // Another tab ran the quiz: pick its answers up.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
      if (event.key === PERSON_TASTE_STORAGE_KEY) byId.value = readAllPersonTaste()
    })
  }

  function persist(next: PersonTasteMap) {
    byId.value = next
    writeAllPersonTaste(next)
  }

  function get(personId: number | null | undefined): PersonTaste {
    if (personId === null || personId === undefined) return emptyPersonTaste()
    return byId.value[String(personId)] ?? emptyPersonTaste()
  }

  function isTuned(personId: number | null | undefined): boolean {
    return isPersonTuned(get(personId))
  }

  function setTaste(personId: number | null | undefined, taste: PersonTaste) {
    if (personId === null || personId === undefined) return
    persist({ ...byId.value, [String(personId)]: normalizePersonTaste(taste) })
  }

  /** Saves only the given axes — a quiz step stores its own answer (T-132). */
  function patchTaste(personId: number | null | undefined, patch: Partial<PersonTaste>) {
    if (personId === null || personId === undefined) return
    setTaste(personId, patchPersonTaste(byId.value[String(personId)], patch))
  }

  const setStyles = (personId: number | null | undefined, styleIds: number[]) => patchTaste(personId, { styleIds })
  const setColors = (personId: number | null | undefined, colorIds: number[]) => patchTaste(personId, { colorIds })
  const setBrands = (personId: number | null | undefined, brandIds: number[]) => patchTaste(personId, { brandIds })

  /** The person was removed from People. */
  function remove(personId: number) {
    if (!(String(personId) in byId.value)) return
    const next = { ...byId.value }
    delete next[String(personId)]
    persist(next)
  }

  return { byId, get, isTuned, setTaste, patchTaste, setStyles, setColors, setBrands, remove }
})
